/* ==========================================================================
   Kkumi Returns · Policy — table-of-contents scroll-spy (custom element)

   Highlights the active TOC link as the reader scrolls through the policy
   blocks. Implemented as a custom element so it survives Shopify theme-editor
   section reloads and supports multiple instances on one page.
   ========================================================================== */

if (!customElements.get('kkumi-returns-policy')) {
  customElements.define(
    'kkumi-returns-policy',
    class KkumiReturnsPolicy extends HTMLElement {
      connectedCallback() {
        this.buffer = parseInt(this.getAttribute('data-offset'), 10) || 24;
        this.fallbackOffset = parseInt(this.getAttribute('data-header-offset'), 10) || 88;
        this.links = Array.prototype.slice.call(
          this.querySelectorAll('.kkumi-returns-policy__toc a')
        );
        this.blocks = Array.prototype.slice.call(
          this.querySelectorAll('.kkumi-returns-policy__block')
        );

        if (!this.links.length || !this.blocks.length) return;

        this._lastActiveId = null;
        this._ticking = false;
        this.headerH = 0;
        this._onScroll = this._onScroll.bind(this);
        this._measureHeader = this._measureHeader.bind(this);
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._measureHeader, { passive: true });
        window.addEventListener('load', this._measureHeader, { passive: true });
        this._measureHeader();
        window.requestAnimationFrame(this._measureHeader);
        setTimeout(this._measureHeader, 300);
        this.spy();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._measureHeader);
        window.removeEventListener('load', this._measureHeader);
      }

      _measureHeader() {
        // The theme's --header-height (from .section-header.offsetHeight) is
        // often 0 on desktop until a 990px breakpoint change, so measure a few
        // candidate header elements and take the tallest rendered one.
        let h = 0;
        ['.kkncl', '.section-header', '.header-wrapper', '.header'].forEach((sel) => {
          const el = document.querySelector(sel);
          if (el) {
            const r = Math.round(el.getBoundingClientRect().height);
            if (r > h) h = r;
          }
        });
        if (h > 0) {
          this.headerH = h;
          this.style.setProperty('--kkr-header-h', h + 'px');
        } else {
          // Layout not ready / custom header — fall back to the configurable
          // clearance so the TOC still clears the header.
          this.headerH = this.fallbackOffset;
          this.style.removeProperty('--kkr-header-h');
        }
        this.spy();
      }

      _onScroll() {
        if (this._ticking) return;
        this._ticking = true;
        window.requestAnimationFrame(() => {
          this.spy();
          this._ticking = false;
        });
      }

      spy() {
        // +4 tolerance: a clicked section lands with its top at the scroll-margin
        // (= header + buffer), so a strict "<" would leave the previous link active.
        const offset = (this.headerH || 0) + this.buffer + 4;
        let active = this.blocks[0];
        this.blocks.forEach((block) => {
          if (block.getBoundingClientRect().top <= offset) {
            active = block;
          }
        });

        // Near the page bottom the last block can't reach the offset line, so
        // force it active — otherwise the final TOC links never highlight.
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
          active = this.blocks[this.blocks.length - 1];
        }

        if (!active || active.id === this._lastActiveId) return;
        this._lastActiveId = active.id;

        this.links.forEach((link) => {
          const isActive = link.getAttribute('href') === '#' + active.id;
          link.classList.toggle('is-active', isActive);
        });
      }
    }
  );
}
